import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ContributorGroupToken } from '@sleed/bizcommerce-api';
import { contributorGroupApi } from '~/lib/api/client';
import { formToToken, type ContributorGroupFormValues } from '~/lib/schemas/contributor-group';

export const contributorGroupKeys = {
	all: ['contributor-groups'] as const,
	list: () => [...contributorGroupKeys.all, 'list'] as const
};

/** All contributor groups (Ομάδες Συντελεστών) — small lookup set, loaded in full. */
export function useContributorGroupsList() {
	return useQuery<ContributorGroupToken[]>({
		queryKey: contributorGroupKeys.list(),
		queryFn: async () => (await contributorGroupApi.getAll()).data.items ?? []
	});
}

export function useContributorGroupMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: contributorGroupKeys.list() });
	const create = useMutation({
		mutationFn: async (form: ContributorGroupFormValues) => (await contributorGroupApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		// Spread the original token first so server-owned fields not on the form (btrl) survive.
		mutationFn: async ({ form, original }: { form: ContributorGroupFormValues; original: ContributorGroupToken | null }) =>
			(await contributorGroupApi.update(formToToken(form, original))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({
		mutationFn: async (id: number) => (await contributorGroupApi.remove(id)).data,
		onSuccess: invalidate
	});
	return { create, update, remove };
}
